/**
 * Inline signup form for the marketing-page hero. Captures step 1
 * (operator: name + email + password) and hands off to /signup which
 * picks up the prefilled fields and goes straight to step 2 (first
 * property / facility). The hero stays compact and the /signup page
 * handles the longer property questionnaire.
 *
 * Per-vertical pages can pass `facility_type` so /signup pre-picks
 * the right vertical chip on step 2.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { trackConversion } from "../../lib/analytics";
import { self } from "../../lib/api";
import { isOperatorAuthenticated, subscribeAuth } from "../../lib/auth";
import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Input } from "../../lib/Input";
import { Slats } from "../../lib/Slats";

type SignupFacilityType =
  | "multifamily"
  | "self_storage"
  | "parking"
  | "vehicle_storage";

interface HeroSignupCardProps {
  /** Optional context blurb above the form — used on /for-X pages to
   * confirm the prospect's vertical landed correctly ("Set up your
   * marina in 60 seconds."). When omitted, falls back to a generic
   * "Start your free trial" framing. */
  contextLine?: string;
  /** Vertical to pre-pick on step 2. Mapped from the /for-X page so
   * the prospect doesn't re-answer "what kind of property" after
   * the page already established it. */
  facilityType?: SignupFacilityType;
}

export function HeroSignupCard({
  contextLine,
  facilityType,
}: HeroSignupCardProps) {
  // Auth-aware branch. When the visitor already has an operator
  // session, the whole "spin up your NestPlot account" pitch is
  // the wrong ask — swap the whole card for "welcome back, go to
  // your dashboard". Subscribe to auth changes so a same-tab
  // sign-in / sign-out re-renders without a page reload
  // (matches MarketingShell). All hooks live at the top of the
  // function so React sees a stable hook order across renders,
  // then we conditionally return the right card body below.
  const [authed, setAuthed] = useState(() => isOperatorAuthenticated());
  useEffect(() => subscribeAuth(() => setAuthed(isOperatorAuthenticated())), []);
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (authed) {
    return <WelcomeBackCard />;
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !email.trim() || !password) {
      setError("Name, email, and password are all required.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    // Hand off to /signup with the step-1 fields prefilled. /signup
    // jumps to step 2 immediately when all three are present and
    // collects the facility details there. No /auth/signup call
    // happens until the operator finishes step 2 — keeps the hero
    // short while still capturing everything the API needs.
    // GTM event: signup_started — the top-of-funnel "they committed
    // to step 1" signal that Google Ads optimizes on for cheap
    // lead-quality bidding.
    trackConversion("signup_started", {
      email: email.trim(),
      vertical: facilityType,
    });
    navigate("/signup", {
      state: {
        op: {
          name: name.trim(),
          email: email.trim(),
          password,
        },
        facility_type: facilityType,
      },
    });
  }

  return (
    <Card>
      {/* Brand slat accent — matches the /pilot card treatment so the
          two main "do something now" surfaces read as siblings. */}
      <div className="relative">
        <Slats color="var(--accent)" opacity={0.7} height={10} />
      </div>
      <div className="px-6 py-6 lg:px-7 lg:py-7">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          Start free · 30 days
        </div>
        <h2 className="font-display font-extrabold text-[22px] tracking-[-0.015em] mt-1">
          {contextLine ?? "Spin up your NestPlot account."}
        </h2>
        <p className="text-sub text-[13.5px] mt-2 leading-relaxed">
          No card required. Two quick steps — operator info here, your
          first property on the next screen. You'll be on the dashboard
          in under 60 seconds.
        </p>

        <form className="mt-5 space-y-3" onSubmit={onSubmit}>
          <Input
            label="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            placeholder="Casey Rivera"
            required
          />
          <Input
            label="Work email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            placeholder="casey@yourproperty.com"
            required
          />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            placeholder="At least 8 characters"
            required
            minLength={8}
          />

          {error && (
            <div className="bg-alert-soft border border-alert/40 text-alert px-3 py-2 rounded-md text-[12.5px]">
              {error}
            </div>
          )}

          <Btn
            type="submit"
            kind="primary"
            size="lg"
            full
            iconRight={<Icon name="arrow-right" size={14} />}
          >
            Continue · next: property details
          </Btn>
        </form>

        <div className="mt-4 text-[12.5px] text-sub">
          Already on NestPlot?{" "}
          <Link to="/signin" className="text-accent font-semibold">
            Sign in
          </Link>
        </div>
      </div>
    </Card>
  );
}

/** Card body rendered when the visitor is already an authenticated
 * operator. Same visual footprint as the guest signup card
 * (matching Slats accent + padding) so the hero layout doesn't
 * jump when auth state flips. Personalizes with `name` from
 * /auth/me when the query succeeds; falls back to "Welcome back"
 * while it's in flight or on a stale token. */
function WelcomeBackCard() {
  // /auth/me is cheap + already cached by react-query if the app
  // shell has loaded it recently. staleTime matches the app-wide
  // 30s (App.tsx queryClient default) so we don't refetch when the
  // marketing surface + app share a tab.
  const me = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => self.me(),
    retry: false,
    staleTime: 30_000,
  });
  const displayName =
    me.data?.name?.trim() ||
    me.data?.email?.split("@")[0] ||
    null;

  return (
    <Card>
      <div className="relative">
        <Slats color="var(--accent)" opacity={0.7} height={10} />
      </div>
      <div className="px-6 py-6 lg:px-7 lg:py-7">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          Signed in
        </div>
        <h2 className="font-display font-extrabold text-[22px] tracking-[-0.015em] mt-1">
          {displayName
            ? `Welcome back, ${displayName}.`
            : "Welcome back."}
        </h2>
        <p className="text-sub text-[13.5px] mt-2 leading-relaxed">
          Pick up where you left off — leads, move-ins, gate codes,
          payouts, and today's queue are all one tap away.
        </p>

        <div className="mt-5 space-y-2">
          <Link to="/app/today">
            <Btn
              kind="primary"
              size="lg"
              full
              iconRight={<Icon name="arrow-right" size={14} />}
            >
              Open dashboard
            </Btn>
          </Link>
          <Link to="/app/leads">
            <Btn kind="ghost" size="md" full>
              Jump to leads
            </Btn>
          </Link>
        </div>

        {me.data?.email && (
          <div className="mt-4 text-[12.5px] text-sub">
            Not you?{" "}
            <Link to="/signin" className="text-accent font-semibold">
              Sign in as a different operator
            </Link>
          </div>
        )}
      </div>
    </Card>
  );
}
